/**
 * Create / edit dialog for an **external** Source (ADR-0026 + ADR-0042).
 *
 * Two connection kinds:
 *  - **Diretta** — a DB credential (postgres / mysql / sqlserver): the cloud dials
 *    the database directly (host/port/user/password live in the credential).
 *  - **Gateway** — the source's I/O runs on an on-prem **Tray Gateway** agent: the
 *    operator picks a gateway + a `remote_ref` (the connection NAME the agent stores);
 *    we create a `tray_gateway` POINTER credential inline (no secret in the cloud).
 *
 * The backend derives the source `kind` from the credential type, so both paths just
 * create a Source bound to a credential.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import * as React from "react";
import { useTranslation } from "react-i18next";

import { CredentialPicker } from "@/components/credentials/CredentialPicker";
import { TagsField } from "@/components/tags";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { credentialsApi, type CredentialType } from "@/lib/api-credentials";
import { gatewaysApi } from "@/lib/api-gateways";
import { sourcesApi, type Source } from "@/lib/api-sources";

/** DB credential types a DIRECT external source can wrap. */
const DB_CREDENTIAL_TYPES: CredentialType[] = ["postgres", "mysql", "sqlserver"] as CredentialType[];

export type DialogMode = { kind: "create" } | { kind: "edit"; source: Source };
type ConnKind = "direct" | "gateway";

export interface SourceDialogProps {
  open: boolean;
  mode: DialogMode;
  onOpenChange: (open: boolean) => void;
}

export function SourceDialog({ open, mode, onOpenChange }: SourceDialogProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const isCreate = mode.kind === "create";
  const editTarget = mode.kind === "edit" ? mode.source : null;

  const [name, setName] = React.useState("");
  const [description, setDescription] = React.useState("");
  const [tags, setTags] = React.useState<string[]>([]);
  const [enabled, setEnabled] = React.useState(true);
  const [credentialId, setCredentialId] = React.useState<string | null>(null);
  const [defaultSchema, setDefaultSchema] = React.useState("");
  // Gateway mode (create only).
  const [connKind, setConnKind] = React.useState<ConnKind>("direct");
  const [gatewayId, setGatewayId] = React.useState<string>("");
  const [remoteRef, setRemoteRef] = React.useState("");
  const [devDsn, setDevDsn] = React.useState("");

  const gatewaysQ = useQuery({
    queryKey: ["gateways"],
    queryFn: gatewaysApi.list,
    enabled: open && connKind === "gateway",
  });

  React.useEffect(() => {
    if (!open) return;
    if (editTarget) {
      setName(editTarget.name);
      setDescription(editTarget.description ?? "");
      setTags(editTarget.tags ?? []);
      setEnabled(editTarget.enabled);
      setCredentialId(editTarget.credential_id ?? null);
      setDefaultSchema((editTarget.config?.default_schema as string) ?? "");
      setConnKind(editTarget.kind === "gateway" ? "gateway" : "direct");
    } else {
      setName("");
      setDescription("");
      setTags([]);
      setEnabled(true);
      setCredentialId(null);
      setDefaultSchema("");
      setConnKind("direct");
      setGatewayId("");
      setRemoteRef("");
      setDevDsn("");
    }
  }, [open, editTarget]);

  const submit = useMutation<Source, Error>({
    mutationFn: async () => {
      const config = defaultSchema.trim() ? { default_schema: defaultSchema.trim() } : {};
      const base = { name: name.trim(), description: description.trim() || null, config, enabled, tags };

      if (isCreate && connKind === "gateway") {
        // DEV-only: wire the gateway to a local DSN via an in-process stub so the source
        // is testable before the real on-prem agent exists (404 in prod — ignored).
        if (import.meta.env.DEV && devDsn.trim()) {
          await gatewaysApi.devStub(gatewayId, remoteRef.trim(), devDsn.trim()).catch(() => {});
        }
        // The pointer credential holds NO secret — just {gateway_id, remote_ref}.
        const cred = await credentialsApi.create({
          type: "tray_gateway",
          name: `${name.trim()} · gateway`,
          config: { gateway_id: gatewayId, remote_ref: remoteRef.trim() },
        });
        return sourcesApi.create({ ...base, credential_id: cred.id });
      }
      if (isCreate) {
        return sourcesApi.create({ ...base, credential_id: credentialId! });
      }
      return sourcesApi.update(editTarget!.id, { ...base, credential_id: credentialId ?? undefined });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["sources"] });
      onOpenChange(false);
    },
  });

  const gatewayValid = gatewayId.length > 0 && remoteRef.trim().length > 0;
  const connValid = isCreate && connKind === "gateway" ? gatewayValid : credentialId !== null;
  const canSubmit = name.trim().length > 0 && connValid && !submit.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="text-sm font-semibold">
            {isCreate
              ? t("sources.dialog.createTitle", "Nuova sorgente")
              : t("sources.dialog.editTitle", "Modifica {{name}}", { name: editTarget?.name })}
          </DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div className="space-y-1.5">
            <Label htmlFor="src-name">{t("sources.field.name", "Nome")}</Label>
            <Input
              id="src-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("sources.field.namePlaceholder", "es. ERP Postgres")}
              autoFocus
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="src-desc">{t("sources.field.description", "Descrizione (opzionale)")}</Label>
            <Input
              id="src-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t("sources.field.descriptionPlaceholder", "A cosa si connette?")}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="src-tags">{t("sources.field.tags", "Tag")}</Label>
            <TagsField id="src-tags" value={tags} onChange={setTags} />
          </div>

          <div className="space-y-2 border-t border-border/60 pt-3">
            <Label>{t("sources.field.connKind", "Connessione")}</Label>
            {isCreate ? (
              <div className="inline-flex rounded-md border border-border/60 p-0.5 text-xs">
                <button
                  type="button"
                  onClick={() => setConnKind("direct")}
                  className={`rounded px-3 py-1 ${connKind === "direct" ? "bg-elevated font-medium" : "text-muted-foreground"}`}
                >
                  {t("sources.connKind.direct", "Diretta")}
                </button>
                <button
                  type="button"
                  onClick={() => setConnKind("gateway")}
                  className={`rounded px-3 py-1 ${connKind === "gateway" ? "bg-elevated font-medium" : "text-muted-foreground"}`}
                >
                  {t("sources.connKind.gateway", "Tramite Gateway")}
                </button>
              </div>
            ) : null}

            {connKind === "gateway" && isCreate ? (
              <div className="space-y-2">
                <div className="space-y-1.5">
                  <Label htmlFor="src-gateway">{t("sources.field.gateway", "Gateway on-prem")}</Label>
                  <select
                    id="src-gateway"
                    value={gatewayId}
                    onChange={(e) => setGatewayId(e.target.value)}
                    className="h-9 w-full rounded-md border border-border bg-surface px-2 text-sm"
                  >
                    <option value="">{t("sources.field.gatewayPick", "— scegli un gateway —")}</option>
                    {(gatewaysQ.data ?? []).map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name} ({g.status})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="src-remoteref">{t("sources.field.remoteRef", "Connessione (remote_ref)")}</Label>
                  <Input
                    id="src-remoteref"
                    value={remoteRef}
                    onChange={(e) => setRemoteRef(e.target.value)}
                    placeholder="erp"
                  />
                  <p className="text-[11px] text-muted-foreground">
                    {t("sources.field.remoteRefHint", "Il nome della connessione definita sull'agente. Il DSN/password restano on-prem.")}
                  </p>
                </div>
                {import.meta.env.DEV ? (
                  <div className="space-y-1.5 rounded-md border border-warning/40 bg-warning/5 p-2">
                    <Label htmlFor="src-devdsn" className="text-warning">
                      {t("sources.field.devDsn", "DSN di test locale (solo sviluppo)")}
                    </Label>
                    <Input
                      id="src-devdsn"
                      value={devDsn}
                      onChange={(e) => setDevDsn(e.target.value)}
                      placeholder="postgresql://user:pass@host:5432/db"
                    />
                    <p className="text-[11px] text-muted-foreground">
                      {t("sources.field.devDsnHint", "Collega il gateway a questo DSN tramite uno stub in-process, così puoi testare senza l'agente reale.")}
                    </p>
                  </div>
                ) : null}
              </div>
            ) : (
              <div className="space-y-1.5">
                <CredentialPicker
                  value={credentialId}
                  onChange={setCredentialId}
                  type={DB_CREDENTIAL_TYPES}
                  emptyText={t(
                    "sources.field.credentialEmpty",
                    "Nessuna credenziale DB — creane una prima in /credentials.",
                  )}
                />
                <p className="text-[11px] text-muted-foreground">
                  {t("sources.field.credentialHint", "Postgres, MySQL o SQL Server.")}
                </p>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="src-schema">{t("sources.field.defaultSchema", "Schema di default")}</Label>
            <Input
              id="src-schema"
              value={defaultSchema}
              onChange={(e) => setDefaultSchema(e.target.value)}
              placeholder="public"
            />
            <p className="text-[11px] text-muted-foreground">
              {t("sources.field.defaultSchemaHint", "Schema su cui parte l'esplorazione. Default: public.")}
            </p>
          </div>

          <label className="flex items-center gap-2 pt-1 text-sm">
            <Switch checked={enabled} onCheckedChange={setEnabled} />
            <span>{t("sources.field.enabled", "Abilitata")}</span>
          </label>

          {submit.error && (
            <Alert tone="danger" title={t("sources.dialog.saveFailed", "Salvataggio fallito")}>
              {(submit.error as Error).message}
            </Alert>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            {t("common.cancel", "Annulla")}
          </Button>
          <Button size="sm" disabled={!canSubmit} onClick={() => submit.mutate()}>
            {submit.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} />}
            {isCreate ? t("sources.dialog.create", "Crea sorgente") : t("common.save", "Salva")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
